/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Card, Checkbox, Col, Divider, Form, Input, Radio, Row, Select, Typography } from 'antd';
import { QuestionCircleOutlined, TeamOutlined } from '@ant-design/icons';
import BotAvatar from '../UI/BotAvatar';
import MultiDropdown from './Fields/MultiDropdown';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { ADDITIONAL_ROLES_ORG, PERMISSIONS_ORG } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreateAutomationMutation, useUpdateAutomationMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';
import useTheme from '../../utils/hooks/useTheme';
import is from '../../utils/is';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

// Constant declarations
const LIVECHAT_PERMISSIONS = [PERMISSIONS_ORG.ADMIN, ADDITIONAL_ROLES_ORG.LIVECHAT_ADMIN];

const AutomationsForm = ({
  form,
  selectedItem = null,
  setSelectedItem,
  hasAdminRights = false,
  setIsModalOpen,
  orgLevelForm = false,
  initialValues = {}
}) => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const { data: automations } = getCurrentQueryState('fetchOrganizationAutomations', { orgId });
  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: bots, isLoading: isLoadingBots } = getCurrentQueryState('fetchBots', { orgId });
  const [updateAutomation] = useUpdateAutomationMutation({ fixedCacheKey: 'updateAutomation' });
  const [createAutomation] = useCreateAutomationMutation({ fixedCacheKey: 'createAutomation' });
  const { t } = useTranslation();
  const { formProps } = useTheme();

  const userPermissions = useUserPermissions(orgId, activeConversationInfo?.tenantId);

  const onSubmitAutomation = async (values) => {
    const valuesToSubmit = {
      name: values.automationName,
      url: values.automationCallbackUrl,
      categories: values.categories,
      headers: values.automationHeaders,
      agentInput: values.runOn?.length ? [] : values.agentInput,
      organizationWide: values.organizationWide,
      runOnMissedRequest: is.truthy(values.runOn.find(item => item === 'runOnMissedRequest')),
      runOnEndConversation: is.truthy(values.runOn.find(item => item === 'runOnEndConversation')),
      tenantIds: values.tenantIds
    };

    valuesToSubmit?.agentInput?.forEach((input, index) => {
      if (input?.type !== 'choice') {
        delete input.entries;
      }
    });

    let isSaveSuccessful;
    if (selectedItem) {
      isSaveSuccessful = await updateAutomation({ orgId, automationId: selectedItem.id, data: valuesToSubmit }).unwrap();
    } else {
      isSaveSuccessful = await createAutomation({ orgId, data: valuesToSubmit }).unwrap();
    }

    if (isSaveSuccessful) {
      if (!selectedItem) {
        setIsModalOpen(false);
        form.resetFields();
      }
    }
  };

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={onSubmitAutomation}
      layout="vertical"
    >
      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Automation Details')}
          </Typography.Text>

          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Set up your automation by providing a button title and the automation URL. This will enable quick access and seamless integration of automated actions.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            name="automationName"
            label={t('Button Title')}
            labelAlign="left"
            tooltip={{
              icon: <QuestionCircleOutlined />,
              title: t('Type here a short title for your Automation button')
            }}
            rules={[
              {
                required: true,
                message: t('Enter a friendly name for this automation')
              }
            ]}
          >
            <Input
              placeholder={t('New automation name')}
              disabled={!hasAdminRights}
            />
          </Form.Item>

          <Form.Item
            name="automationCallbackUrl"
            label={t('Automation URL')}
            className="mb-0"
            required
            labelAlign="left"
            tooltip={{
              icon: <QuestionCircleOutlined />,
              title: t('Type the URL that will be used for the Automation')
            }}
            rules={[
              {
                validator: async (_rule, value) => {
                  try {
                    const callbackUrl = new URL(value);
                    if (callbackUrl) {
                      return Promise.resolve();
                    }
                  } catch (ex) {
                    return Promise.reject(new Error(t('Enter a valid URL')));
                  }
                }
              }
            ]}
          >
            <Input
              placeholder={t('Enter the automation URL')}
              disabled={!hasAdminRights}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Add Categories')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Use tags to organize and categorize your automations efficiently.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            name="categories"
            label={t('Insert Tag Categories')}
            labelAlign="left"
            className="mb-0"
          >
            <Select
              mode="tags"
              tokenSeparators={[',']}
              placeholder={t('Add a category or a list of categories')}
              // Options are all categories from all automations
              options={
                (automations || [])
                  .map(automation => automation.categories) // Get all categories
                  .flat() // Flatten the array to a single array
                  .filter((category, index, self) => category && self.indexOf(category) === index) // Remove duplicates (and empty strings)
                  .map(category => ({ label: category, value: category }))
              }
              disabled={!hasAdminRights}
              onInputKeyDown={(e) => {
                if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                  e.preventDefault();
                  return false;
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Behavior')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Set the conditions for automatic execution by choosing when the automation should run.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            name="runOn"
            label={<Typography.Text>{t('This automation will run automatically')}</Typography.Text>}
            labelCol={{ span: 24 }}
            labelAlign="left"
            className="mb-0"
          >
            <Checkbox.Group
              disabled={![...LIVECHAT_PERMISSIONS].some(permission => userPermissions.includes(permission))}
              options={[
                {
                  label: <Typography.Text>{t('On missed chat')}</Typography.Text>,
                  value: 'runOnMissedRequest'
                },
                {
                  label: <Typography.Text>{t('On terminated chat')}</Typography.Text>,
                  value: 'runOnEndConversation'
                }
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Visibility & Availability')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Control who can view & use this automation.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            name="organizationWide"
            label={<Typography.Text>{t('Who can view & use this automation')}</Typography.Text>}
            labelCol={{ span: 24 }}
            labelAlign="left"
          >
            <Radio.Group
              disabled={!LIVECHAT_PERMISSIONS.some(permission => userPermissions.includes(permission))}
            >
              <Radio value={false}>{t('Only owner')}</Radio>
              <Radio value={true}>{t('All Workspace users')} <TeamOutlined /></Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="tenantIds"
            label={<Typography.Text>{t('This automation will be available for')}</Typography.Text>}
            labelCol={{ span: 24 }}
            labelAlign="left"
            className="mb-0"
          >
            {
              orgLevelForm ? (
                <MultiDropdown
                  size="large"
                  placeholder={t('Select agents')}W
                  allowClear
                  loading={isLoadingBots}
                  autoClearSearchValue={false}
                  hideExtraFromDropdown
                  options={(bots?.items || []).map(bot => ({
                    label: <BotAvatar bot={bot} wrapBotName maxNameCharacters={28} className="d-inline-block w-auto" />,
                    value: bot.id,
                    searchValue: bot.displayName
                  }))}
                />
              ) : (
                <Radio.Group
                  disabled={!hasAdminRights}
                >
                  <Radio value={activeConversationInfo?.tenantId}>{activeConversationInfo?.tenantName}</Radio>
                  <Radio value={null}>{t('All agents')}</Radio>
                </Radio.Group>
              )
            }
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('LiveChat Agent Input')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Add LiveChat Agent Inputs.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.Item
            shouldUpdate
            className="mb-2 py-2 "
          >
            {() => (
              <Form.List
                name="agentInput"
              >
                {(fields, { add, remove }, { errors }) => (
                  <Fragment>
                    {
                      !fields?.length ? (
                        <Typography.Paragraph
                          className="fs-sm text-muted fw-bold text-center m-auto p-1"
                        >
                          {t('Add inputs here')}
                        </Typography.Paragraph>
                      ) : null
                    }

                    {
                      fields.map(({ key, name, fieldKey, ...restField }) => (
                        <Row key={key} gutter={8} wrap={false}>
                          <Col flex="auto">
                            <Card
                              className="mb-2 shadow-none bg-layout position-relative"
                              key={key}
                            >
                              <Form.Item
                                key={key}
                                className="m-0"
                              >
                                <Row gutter={8} wrap={false}>
                                  <Col flex="1 1 100%">
                                    <Form.Item
                                      colon={false}
                                      {...restField}
                                      name={[name, 'name']}
                                      className="mb-1"
                                      label={t('Field Name')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                      fieldKey={[fieldKey, 'name']}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('Enter a name')
                                        }
                                      ]}
                                    >
                                      <Input
                                        placeholder={t('Field name')}
                                        disabled={form.getFieldValue('runOn')?.length}
                                        autoFocus
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col flex="1 1 100%">
                                    <Form.Item
                                      className="mb-1"
                                      colon={false}
                                      {...restField}
                                      name={[name, 'prompt']}
                                      label={t('Field Prompt')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                      fieldKey={[fieldKey, 'prompt']}
                                      rules={[
                                        {
                                          required: true,
                                          message: t('Enter a prompt')
                                        }
                                      ]}
                                    >
                                      <Input
                                        placeholder={t('Prompt message')}
                                        disabled={form.getFieldValue('runOn')?.length}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col flex="none">
                                    <Form.Item
                                      className="mb-1"
                                      colon={false}
                                      {...restField}
                                      name={[name, 'isRequired']}
                                      label={t('Required')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                      fieldKey={[fieldKey, 'isRequired']}
                                      valuePropName="checked"
                                      initialValue={false}
                                    >
                                      <Checkbox
                                        disabled={form.getFieldValue('runOn')?.length}
                                      />
                                    </Form.Item>
                                  </Col>
                                </Row>

                                <Row gutter={8} wrap={false}>
                                  <Col span={7} flex="1 1 100%">
                                    <Form.Item
                                      className="mb-1"
                                      colon={false}
                                      {...restField}
                                      name={[name, 'type']}
                                      label={t('Field Type')}
                                      labelAlign="left"
                                      labelCol={{ xs: 24 }}
                                      initialValue={'plain_text'}
                                      fieldKey={[fieldKey, 'type']}
                                    >
                                      <Select
                                        options={[
                                          {
                                            label: t('Plain text'),
                                            value: 'plain_text'
                                          },
                                          {
                                            label: t('Email'),
                                            value: 'email'
                                          },
                                          {
                                            label: t('Number'),
                                            value: 'number'
                                          },
                                          {
                                            label: t('Phone number'),
                                            value: 'phone_number'
                                          },
                                          {
                                            label: t('Date'),
                                            value: 'date'
                                          },
                                          {
                                            label: t('Dropdown'),
                                            value: 'choice'
                                          }
                                        ]}
                                        disabled={form.getFieldValue('runOn')?.length}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col span={16} flex="1 1 100%">
                                    {form.getFieldValue(['agentInput', name, 'type']) === 'choice' && (
                                      <Fragment>
                                        <Form.List name={[name, 'entries']}>
                                          {(dropdownFields, { add: addDropdownOption, remove: removeDropdownOptions }) => (
                                            <Fragment>
                                              {
                                                dropdownFields.map(({ key: dropdownKey, name: dropdownName, ...restDropdownField }) => (
                                                  <Form.Item key={dropdownKey} className="m-0">
                                                    <Row gutter={12} wrap={false}>
                                                      <Col span={10} flex="1 1 100%">
                                                        <Form.Item
                                                          className="mb-1"
                                                          colon={false}
                                                          labelAlign="left"
                                                          labelCol={{ xs: 24 }}
                                                          label="Key"
                                                          {...restDropdownField}
                                                          name={[dropdownName, 'key']}
                                                          rules={[
                                                            {
                                                              required: true,
                                                              message: t('Enter a key')
                                                            }
                                                          ]}
                                                        >
                                                          <Input placeholder={t('Key')} disabled={form.getFieldValue('runOn')?.length} />
                                                        </Form.Item>
                                                      </Col>

                                                      <Col span={10} flex="1 1 100%">
                                                        <Form.Item
                                                          className="mb-1"
                                                          colon={false}
                                                          labelAlign="left"
                                                          labelCol={{ xs: 24 }}
                                                          label="Value"
                                                          {...restDropdownField}
                                                          name={[dropdownName, 'value']}
                                                          rules={[
                                                            {
                                                              required: true,
                                                              message: t('Enter a value')
                                                            }
                                                          ]}
                                                        >
                                                          <Input placeholder={t('Value')} disabled={form.getFieldValue('runOn')?.length} />
                                                        </Form.Item>
                                                      </Col>

                                                      <Col span={4} flex="none">
                                                        <Form.Item
                                                          className="mb-1"
                                                          label={<Typography.Text className="d-none">{t('Delete')}</Typography.Text>}
                                                          labelAlign="left"
                                                          colon={false}
                                                          labelCol={{ xs: 24 }}
                                                        >
                                                          <Button
                                                            shape="circle"
                                                            type="text"
                                                            className="text-secondary"
                                                            icon={<DeleteIcon />}
                                                            onClick={() => removeDropdownOptions(dropdownName)}
                                                            disabled={!hasAdminRights}
                                                          />
                                                        </Form.Item>
                                                      </Col>
                                                    </Row>
                                                  </Form.Item>
                                                ))
                                              }

                                              <Form.Item
                                                label={<Typography.Text className="d-none">{t('Delete')}</Typography.Text>}
                                                labelAlign="left"
                                                colon={false}
                                                labelCol={{ xs: 24 }}
                                              >
                                                {/* The hidden Form.Item wrapper is needed to properly align the button */}
                                                <Button
                                                  block
                                                  type="dashed"
                                                  onClick={() => addDropdownOption()}
                                                  icon={<PlusIcon />}
                                                  disabled={form.getFieldValue('runOn')?.length}
                                                >
                                                  {t('Add Pair')}
                                                </Button>
                                              </Form.Item>
                                            </Fragment>
                                          )}
                                        </Form.List>
                                      </Fragment>
                                    )}
                                  </Col>
                                </Row>
                              </Form.Item>
                            </Card>
                          </Col>

                          <Col flex="none">
                            <Button
                              shape="circle"
                              type="text"
                              size="large"
                              className="text-secondary"
                              icon={<DeleteIcon />}
                              onClick={() => remove(name)}
                            />
                          </Col>
                        </Row>
                      ))
                    }

                    <Button
                      type="dashed"
                      onClick={() => add()}
                      icon={<PlusIcon />}
                      disabled={form.getFieldValue('runOn')?.length}
                    >
                      {t('Add Field')}
                    </Button>
                  </Fragment>
                )}
              </Form.List>
            )}
          </Form.Item>

        </Col>
      </Row>

      <Divider />

      <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
        <Col xs={formProps.mainColSpan}>
          <Typography.Text>
            {t('Custom Headers')}
          </Typography.Text>
          <Typography.Paragraph className="fs-sm" type="secondary">
            {t('Add Custom Headers here.')}
          </Typography.Paragraph>
        </Col>

        <Col xs={formProps.secondaryColSpan}>
          <Form.List
            name="automationHeaders"
          >
            {(fields, { add, remove }, { errors }) => (
              <Fragment>
                {
                  fields?.length ? (
                    <Row wrap={false} gutter={12}>
                      <Col xs={11}>
                        <Typography.Paragraph ellipsis>
                          {t('Header name')}
                        </Typography.Paragraph>
                      </Col>
                      <Col xs={11}>
                        <Typography.Paragraph ellipsis>
                          {t('Header value')}
                        </Typography.Paragraph>
                      </Col>
                    </Row>
                  ) : (
                    <Form.Item
                      label={<Typography.Text>{t('Add custom headers here')}</Typography.Text>}
                      labelAlign="left"
                      labelCol={{ span: 24 }}
                      tooltip={{ icon: <QuestionCircleOutlined />, title: t('Add custom headers to your request before triggering the Automation') }}
                      className="mb-0"
                    />
                  )
                }

                <div>
                  {
                    fields.map(({ key, name, ...restField }) => (
                      <Form.Item
                        key={key}
                        className="m-0"
                      >
                        <Row gutter={12} wrap={false}>
                          <Col flex="auto">
                            <Form.Item
                              {...restField}
                              name={[name, 'key']}
                              rules={[
                                {
                                  required: true,
                                  message: t('Enter a header key')
                                }
                              ]}
                            >
                              <Input
                                placeholder={t('Header key')}
                                disabled={!hasAdminRights}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="auto">
                            <Form.Item
                              {...restField}
                              name={[name, 'value']}
                              rules={[
                                {
                                  required: true,
                                  message: t('Enter a header value')
                                }
                              ]}
                            >
                              <Input
                                placeholder={t('Header value')}
                                disabled={!hasAdminRights}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex="none">
                            <Button
                              shape="circle"
                              type="text"
                              className="text-secondary"
                              icon={<DeleteIcon />}
                              onClick={() => remove(name)}
                              disabled={!hasAdminRights}
                            />
                          </Col>
                        </Row>
                      </Form.Item>
                    ))
                  }
                </div>

                <Button
                  type="dashed"
                  onClick={() => add({ key: '', value: '' })}
                  icon={<PlusIcon />}
                  disabled={!hasAdminRights}
                >
                  {t('Add Header')}
                </Button>
              </Fragment>
            )}
          </Form.List>
        </Col>
      </Row>
    </Form>
  );
};

AutomationsForm.propTypes = {
  form: PropTypes.object,
  selectedItem: PropTypes.object,
  setSelectedItem: PropTypes.func,
  hasAdminRights: PropTypes.bool,
  setIsModalOpen: PropTypes.func,
  orgLevelForm: PropTypes.bool,
  initialValues: PropTypes.object
};

export default AutomationsForm;
